import { useEffect, useMemo, useReducer, useRef } from 'react'

import { useNavigate, useSearchParams } from 'react-router-dom'

import {
  fetchChatToken,
  fetchConversation,
  fetchConversationsList,
  fetchUnreadList,
  joinChat,
} from '@app/api'

import { IChatContext } from '@app/context'
import {
  getConversationItem,
  getUnreadStatusEnabled,
  isSameConversation,
  spamStatuses,
} from '@app/helpers'
import { Conversation, ConversationItem } from '@app/models'

import { ABORT_REASON_403_REINIT } from '@app/services/api/constants'
import {
  closeConversationAndNavigateToMessages,
  handleForbiddenConversationError,
} from '@app/utils'
import { isCanceledError } from '@app/utils/errors'

import {
  initialChatState,
  CONVERSATIONS_LIST_SIZE,
  REMINDER_CAMPAIGN,
  REMINDER_CONTENT,
} from './constants'

import { chatReducer } from './reducer'

import { ConversationsListStatus } from './types'
import { useChatSearchParams } from './useChatSearchParams'
import { useConversationID } from '../conversation/useConversationID'
import { useAccess } from '../useAccess'
import { useDependencies } from '../useDependencies'
import { useUser } from '../user/useUser'

export const useChatState = (): IChatContext => {
  const userState = useUser()
  const hasChatAccess = useAccess()

  const {
    queryStatus,
    queryReplyDetectionStatus,
    utmTags,
    replyFormPushReminder,
  } = useChatSearchParams()

  const [state, dispatch] = useReducer(chatReducer, {
    ...initialChatState,
    status: queryStatus,
    replyDetectionStatus: queryReplyDetectionStatus,
  })

  useEffect(() => {
    dispatch({
      type: 'SET_CONVERSATIONS_LIST_STATUS',
      payload: queryStatus,
    })
    dispatch({
      type: 'SET_REPLY_DETECTION_STATUS',
      payload: queryReplyDetectionStatus,
    })
  }, [queryStatus, queryReplyDetectionStatus])

  const navigate = useNavigate()

  const conversationID = useConversationID()
  const { currentUser } = userState
  const { getDependencies } = useDependencies({ current: state })
  const initialLoadRef = useRef(true)
  const previousFetchParamsRef = useRef({
    conversationID,
    status: state.status,
    replyDetectionStatus: state.replyDetectionStatus,
  })
  const fetchGenerationRef = useRef(0)

  const [searchParams, setSearchParams] = useSearchParams()
  const hasReplyDetectionStatusQueryParam =
    searchParams.get('replied') === 'true' ||
    searchParams.get('replied') === 'false'
  if (
    conversationID &&
    utmTags.utm_campaign === REMINDER_CAMPAIGN &&
    utmTags.utm_content === REMINDER_CONTENT &&
    !utmTags.utm_email_reply_conversation_id
  ) {
    const newParams = new URLSearchParams(searchParams)
    newParams.set('utm_email_reply_conversation_id', conversationID.toString())
    setSearchParams(newParams, { replace: true })
  }

  if (
    conversationID &&
    replyFormPushReminder === 'true' &&
    !utmTags.utm_push_reply_conversation_id
  ) {
    const newParams = new URLSearchParams(searchParams)
    newParams.set('utm_push_reply_conversation_id', conversationID.toString())
    setSearchParams(newParams, { replace: true })
  }

  const fetchInitialState = async (generation: number) => {
    dispatch({ type: 'FETCH_INITIAL_STATE_START' })
    let conversationNotFound = false
    const isInitialLoad = initialLoadRef.current
    try {
      const isSpam = state.status === 'spam'
      const statuses = isSpam
        ? spamStatuses(userState)
        : (['dispatched'] as ConversationsListStatus[])
      const replyDetectionStatus = !isSpam
        ? state.replyDetectionStatus
        : undefined
      const spamListEnabled = !isSpam
      const unreadEnabled = getUnreadStatusEnabled(userState)

      const [
        chatTokenResponse,
        conversationsListResponse,
        spamConversationsListResponse,
        unreadSummary,
      ] = await Promise.all([
        fetchChatToken(),
        fetchConversationsList(
          '0',
          CONVERSATIONS_LIST_SIZE,
          statuses,
          replyDetectionStatus
        ),
        spamListEnabled
          ? fetchConversationsList('0', 1, spamStatuses(userState))
          : Promise.resolve({ conversations: [] }),
        unreadEnabled
          ? fetchUnreadList(statuses)
          : Promise.resolve({ unreadList: [], total: 0 }),
      ])

      if (generation !== fetchGenerationRef.current) return

      const unreadList = unreadSummary.unreadList

      const conversations: ConversationItem[] = conversationsListResponse.conversations.map((item) => {
        const previousConversation = state.conversations[item.id]
        return getConversationItem(
          item,
          chatTokenResponse.participantIds,
          unreadList,
          previousConversation
        )
      })

      const hasSpam =
        spamConversationsListResponse.conversations.length > 0 ||
        (isSpam && conversations.length > 0)

      /**
       * NOTE:
       * If selected conversation out of page, fetch it and push to list response
       */
      if (
        conversationID &&
        !conversations.some((item) => isSameConversation(item, conversationID))
      ) {
        let conversation: Conversation | null = null
        try {
          conversation = await fetchConversation(conversationID)
        } catch (error) {
          if (generation !== fetchGenerationRef.current) return

          if ((error as import('axios').AxiosError)?.response?.status === 404) {
            conversationNotFound = true
          } else if (
            !handleForbiddenConversationError(error, () => {
              closeConversationAndNavigateToMessages(navigate)
            })
          ) {
            throw error
          }
        }

        if (generation !== fetchGenerationRef.current) return

        if (conversation) {
          const spamStatusesSet = new Set(['Spam', 'DispatchedAndSpam'])
          const matchesFilters = isSpam
            ? spamStatusesSet.has(conversation.status)
            : !spamStatusesSet.has(conversation.status) &&
              (!replyDetectionStatus ||
                conversation.replyDetectionStatus === replyDetectionStatus)

          const requiresSpamFilter = spamStatusesSet.has(conversation.status)

          if (isInitialLoad) {
            const params = new URLSearchParams(searchParams)
            let paramsUpdated = false

            if (requiresSpamFilter && !isSpam) {
              params.set('status', 'spam')
              paramsUpdated = true
            }

            if (!requiresSpamFilter && isSpam) {
              params.delete('status')
              paramsUpdated = true
            }

            if (
              hasReplyDetectionStatusQueryParam &&
              (!replyDetectionStatus ||
                conversation.replyDetectionStatus !== replyDetectionStatus)
            ) {
              params.delete('replied')
              paramsUpdated = true
            }

            if (paramsUpdated) {
              setSearchParams(params, { replace: true })
            }
          }

          if (!matchesFilters && !isInitialLoad) {
            conversation = null
          }
        }

        /**
         * NOTE:
         * If conversation without acsChatThreadId and current user is participant, join chat with fetching acsChatThreadId
         */
        if (conversation) {
          if (
            !conversation.acsChatThreadId &&
            conversation.participants.some(
              (item) => item.userId === currentUser.id
            )
          ) {
            const { acsChatThreadId } = await joinChat(
              chatTokenResponse.acsChatParticipantId,
              chatTokenResponse.acsChatToken.token,
              conversation.id
            )

            conversation = { ...conversation, acsChatThreadId }
          }

          const previousConversation = state.conversations[conversation.id]
          conversations.push(
            getConversationItem(
              conversation,
              chatTokenResponse.participantIds,
              unreadList,
              previousConversation
            )
          )
        }
      }

      const { listings, accounts } = await getDependencies(conversations)

      if (generation !== fetchGenerationRef.current) return

      dispatch({
        type: 'FETCH_INITIAL_STATE_SUCCESS',
        payload: {
          acsChatParticipantId: chatTokenResponse.acsChatParticipantId,
          acsChatToken: chatTokenResponse.acsChatToken,
          participantIds: chatTokenResponse.participantIds,
          conversations,
          listings,
          accounts,
          nextPageCursor: conversationsListResponse.nextPageCursor,
          unreadList,
          unreadTotal: unreadSummary.total,
          hasSpam,
        },
      })
      if (conversationNotFound) {
        closeConversationAndNavigateToMessages(navigate)
      }
    } catch (error) {
      if (generation !== fetchGenerationRef.current) return

      if (isCanceledError(error)) {
        const signal = (error as import('axios').AxiosError)
          ?.config?.signal as (AbortSignal & { reason?: unknown }) | undefined
        const abortReason = signal?.reason

        if (abortReason === ABORT_REASON_403_REINIT && conversationID) {
          closeConversationAndNavigateToMessages(navigate)
        }

        return
      }

      if (
        error instanceof Error &&
        error.message === 'App is reinitializing after 403'
      ) {
        return
      }

      console.error('An error occurred:', error)
      const errorStatus = (error as import('axios').AxiosError)?.response
        ?.status

      if (errorStatus === 403 && conversationID) {
        closeConversationAndNavigateToMessages(navigate)
        return
      }

      dispatch({
        type: 'FETCH_INITIAL_STATE_ERROR',
        payload: { status: errorStatus },
      })
    }

    initialLoadRef.current = false
  }

  useEffect(() => {
    const generation = ++fetchGenerationRef.current
    const previousParams = previousFetchParamsRef.current
    const onlyConversationChanged =
      previousParams.conversationID !== conversationID &&
      previousParams.status === state.status &&
      previousParams.replyDetectionStatus === state.replyDetectionStatus
    previousFetchParamsRef.current = {
      conversationID,
      status: state.status,
      replyDetectionStatus: state.replyDetectionStatus,
    }

    if (!hasChatAccess) {
      return
    }

    if (
      onlyConversationChanged &&
      state.fetching === 'SUCCEED' &&
      (conversationID === null ||
        Object.values(state.conversations).some((conversation) =>
          isSameConversation(conversation, conversationID)
        ))
    ) {
      return
    }

    fetchInitialState(generation)

    return () => {
      if (fetchGenerationRef.current === generation) {
        fetchGenerationRef.current++
      }
    }
  }, [hasChatAccess, state.status, state.replyDetectionStatus, conversationID])

  return useMemo(
    () => ({
      state,
      dispatch,
    }),
    [state]
  )
}
